import { useMemo } from 'react';

import { ApolloError, gql } from '@apollo/client';

import { useSelfScheduleTemplatesQuery } from 'src/generated/mloop-graphql';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SelfScheduleTemplatesQuery = gql`
  query SelfScheduleTemplates($jobStageId: uuid!) {
    jobStage(id: $jobStageId) {
      jobStageSettingsV2 {
        interviewerEventTemplateId {
          templateId
        }
        candidateEventTemplateId {
          templateId
        }
        candidateEmailTemplateId {
          templateId
        }
      }
    }
  }
`;

interface Templates {
  interviewerEventTemplateId?: string;
  candidateEventTemplateId?: string;
  candidateEmailTemplateId?: string;
}

/**
 * This function is used to get the default templates for the self schedule modal flow.
 * It will first try to get the templates from the job stage settings, if they are not set
 * it will use the default templates for the organization.
 */
export const useSelfScheduleJobStageTemplates = (
  jobStageId: string
): { templates: Templates; loading: boolean; error: ApolloError | undefined } => {
  const {
    data,
    loading: jobTemplatesLoading,
    error: jobTemplatesError,
  } = useSelfScheduleTemplatesQuery({
    variables: {
      jobStageId,
    },
    skip: !jobStageId,
  });

  const defaultTemplates = useMemo(() => {
    if (jobTemplatesLoading || jobTemplatesError) {
      return {
        interviewerEventTemplateId: '',
        candidateEventTemplateId: '',
        candidateEmailTemplateId: '',
      };
    }

    let templates = {
      interviewerEventTemplateId: '',
      candidateEventTemplateId: '',
      candidateEmailTemplateId: '',
    };

    const jobStageSettings = data?.jobStage?.jobStageSettingsV2;
    if (jobStageSettings) {
      if (jobStageSettings.interviewerEventTemplateId) {
        templates = {
          ...templates,
          interviewerEventTemplateId: jobStageSettings.interviewerEventTemplateId?.templateId,
        };
      }

      if (jobStageSettings.candidateEventTemplateId) {
        templates = {
          ...templates,
          candidateEventTemplateId: jobStageSettings.candidateEventTemplateId?.templateId,
        };
      }

      if (jobStageSettings.candidateEmailTemplateId) {
        templates = {
          ...templates,
          candidateEmailTemplateId: jobStageSettings.candidateEmailTemplateId?.templateId,
        };
      }
    }

    return templates;
  }, [data, jobTemplatesLoading, jobTemplatesError]);

  return {
    templates: defaultTemplates,
    loading: jobTemplatesLoading,
    error: jobTemplatesError,
  };
};
